import React, { Dispatch, SetStateAction } from "react";
import { CoObjectItem, SheetForm } from "../../interfaces/CoObject";
import { formatMessage } from "../../../utils/helpers/LanguageHelper";
import { CustomModal } from "../../layout/ActionModal";
import { OperationCompletedPanel } from "../../layout/OperationCompletedPanel";
import HeaderPanel from "../../layout/HeaderPanel";
import { PivotObject } from "../../generic/PivotObject";
import useObjectPanelComponent, { getInitialTabs } from "./ObjectPanelComponentHook";
import { ContentObject } from "./ContentObject";
import { FooterPanelObject } from "./FooterPanelObject";
import { ObjectTitleWithComponentFlag } from "./ObjectTitleWithComponentFlag";
import { DocumentPartMetadata } from "../../../api/models/document-part-metadata";
import { DocumentPart, FormSheet } from "../../../api/models";
import { isComponentInstance } from "../../../utils/helpers/ComponentInstanceHelper";

// TODO: this section is necessary for the future development (actual DEV-104480)
export interface TableUpdateStrategy {
  typeUpdate: "update" | "fullReset" | "keepAllChanges";
  overrideData: boolean;
  overrideStyle: boolean;
  originalPayload?: string;
}

export interface ObjectPanelPivotProps {
  isEdit: boolean;
  isLocked: boolean;
  saveCoObject: (obj: CoObjectItem, validator: ObjectPanelValidator) => void;
  coObject: CoObjectItem;
  documentPart: DocumentPart;
  documentPartMetadata: DocumentPartMetadata;
  onSaveBtn: (tableUpdateType: TableUpdateStrategy["typeUpdate"]) => void;
  validator: ObjectPanelValidator;
  originalSheetArea?: FormSheet;
  setOriginalSheetArea?: Dispatch<SetStateAction<SheetForm>>;
  isOldSheetOrAreaSelected?: () => boolean;
  isLoadingSheetAndArea?: boolean;
}

export interface ObjectPanelTabs {
  key: string;
  name: string;
  isValid: boolean;
}

export interface ObjectPanelValidator {
  isAllValid: boolean;
  modifiedFields: boolean;
  modifiedSource: boolean;
}

export const ObjectPanelComponent: React.FunctionComponent<ObjectPanelPivotProps> = (props) => {
  const {
    getHeaderTitle,
    modalProps,
    deleted,
    isEdit,
    updateWithComponentData,
    selectedKey,
    onClickPivot,
    tabs,
    getBreadcrumbTitle,
    coObject,
    documentPartMetadata,
    updateLockObjectInfo,
    updateObjectInfo,
    updateObjectSource,
    updateObjectAdvanced,
    updateObjectParameters,
    validateAndUpdateSmartObject,
    inheritableParametersInThisDb,
    availableParametersMetadata,
    datasetFields,
    saveAndNavigate,
    onUnlockObject,
    onNavigate,
    goToTabIndex,
    checkInsertBtnEnabled,
    checkNextBtnVisible,
    checkUpdateBtnEnabled,
    checkUpdateBtnVisible,
    setDeletionConfirm,
    refreshClickHandler,
    checkInsertBtnVisible,
    checkNextBtnEnabled,
    checkSaveBtnEnabled,
    checkSaveBtnVisible,
    checkDeleteBtnEnabled,
    isLocked,
    isFileExcelExisting,
    fileExcelName,
    updateFileExcelName,
    tableUpdateType,
    setTableUpdateType,
    originalSheetArea,
    setOriginalSheetArea,
    isOldSheetOrAreaSelected,
    setIsOldSheetOrAreaSelected,
    isLoadingSheetAndArea,
    setIsLoadingSheetAndArea,
    setIsSheetChartAndAreaToHide,
    isSheetChartAndAreaToHide,
    referringScenarios,
    availableDimensionsMetadata,
  } = useObjectPanelComponent(props);

  return (
    <>
      <HeaderPanel
        applyPadding
        headerRowJustifyContent={"space-between"}
        borderColor={
          (coObject.info?.isComponent || isComponentInstance(coObject)) && !deleted ? "var(--color-purple)" : undefined
        }
      >
        <ObjectTitleWithComponentFlag
          titleProvider={getHeaderTitle}
          coObjectItem={coObject}
          updateCoObjectInfo={updateObjectInfo}
          isDeleted={deleted}
          isEdit={isEdit}
          isLocked={isLocked}
          updateLockObjectInfo={updateLockObjectInfo}
        />
      </HeaderPanel>

      {modalProps && <CustomModal {...modalProps} />}
      {deleted ? (
        <OperationCompletedPanel
          message={formatMessage("cof.createnewobject.modal.successmessage")}
          alt={formatMessage("cof.createnewobject.modal.successmessage")}
          icon="/assets/trash-3x.png"
        />
      ) : (
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
              <PivotObject
                isEdit={isEdit}
                isComponentInstance={isComponentInstance(coObject)}
                selectedKey={selectedKey}
                onClickPivot={onClickPivot}
                items={tabs}
                defaultTabName={"info"}
              />
              <div style={{ marginBottom: "20px" }}>
                {coObject ? (
                  <ContentObject
                    getBreadcrumbTitle={getBreadcrumbTitle}
                    isEdit={isEdit}
                    isLocked={coObject.isLocked}
                    isComponentInstance={isComponentInstance(coObject)}
                    updateWithComponentData={updateWithComponentData}
                    selectedKey={selectedKey}
                    documentPartMetadata={documentPartMetadata}
                    objectInfo={coObject.info}
                    updateObjectInfo={updateObjectInfo}
                    objectSource={coObject.source}
                    updateObjectSource={(obj, tabsValid, modified) => updateObjectSource(obj, tabsValid, modified)}
                    objectAdvanced={coObject.advanced}
                    updateObjectAdvanced={(obj, tabsValid, modified) => updateObjectAdvanced(obj, tabsValid, modified)}
                    objectParameters={coObject.parameters}
                    updateObjectParameters={(obj, tabsValid, modified) =>
                      updateObjectParameters(obj, tabsValid, modified)
                    }
                    onPasteSmartObject={(smartObject) => validateAndUpdateSmartObject(smartObject)}
                    inheritableParameters={inheritableParametersInThisDb}
                    availableParametersMetadata={availableParametersMetadata}
                    datasetFields={datasetFields}
                    items={tabs}
                    isFileExcelExisting={isFileExcelExisting}
                    fileExcelName={fileExcelName}
                    updateFileExcelName={updateFileExcelName}
                    originalSheetArea={originalSheetArea}
                    setOriginalSheetArea={setOriginalSheetArea}
                    setIsOldSheetOrAreaSelected={setIsOldSheetOrAreaSelected}
                    setIsLoadingSheetAndArea={setIsLoadingSheetAndArea}
                    setIsSheetChartAndAreaToHide={setIsSheetChartAndAreaToHide}
                    isSheetChartAndAreaToHide={isSheetChartAndAreaToHide}
                    referringScenarios={referringScenarios}
                    availableDimensionsMetadata={availableDimensionsMetadata}
                  />
                ) : null}
              </div>
              <FooterPanelObject
                isEdit={isEdit}
                isComponentInstance={isComponentInstance(coObject)}
                selectedKey={selectedKey}
                tabsScrollView={coObject.info?.componentReference ? getInitialTabs(true) : tabs}
                onSaveCoObject={saveAndNavigate}
                onNavigate={onNavigate}
                goToTabIndex={goToTabIndex}
                isInsertBtnEnabled={checkInsertBtnEnabled()}
                isInsertBtnVisible={checkInsertBtnVisible()}
                isNextBtnEnabled={checkNextBtnEnabled()}
                isNextBtnVisible={checkNextBtnVisible()}
                isSaveEnabled={checkSaveBtnEnabled()}
                isSaveVisible={checkSaveBtnVisible()}
                isUpdateEnabled={checkUpdateBtnEnabled()}
                isUpdateVisible={checkUpdateBtnVisible()}
                isDeleteEnabled={checkDeleteBtnEnabled()}
                onDelete={setDeletionConfirm}
                refreshClickHandler={refreshClickHandler}
                isFileExcelExisting={isFileExcelExisting}
                isOldSheetOrAreaSelected={isOldSheetOrAreaSelected}
                isLoadingSheetAndArea={isLoadingSheetAndArea}
                // TODO: this section is necessary for the future development (actual DEV-104480)
                tableUpdateType={tableUpdateType}
                setTableUpdateType={setTableUpdateType}
                isObjectTableOrChart={
                  (coObject.info?.objectType === "TABLE" || coObject.info?.objectType === "CHART") &&
                  coObject.info?.componentReference === undefined
                }
                isLocked={coObject.isLocked}
                onUnlockObject={onUnlockObject}
                isSheetChartAndAreaToHide={isSheetChartAndAreaToHide}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
